"use client";
import React, { createContext, useContext, useState, useCallback } from "react";

import { UserJourneyData } from "../types/dataSchema";

// Type for public data without sensitive admin information
type PublicJourneyData = Omit<UserJourneyData, "modificationHistory">;

interface JourneyDataContextType {
  userData: PublicJourneyData;
  refresh: () => Promise<void>;
  updateUserData: (newData: PublicJourneyData) => void;
}

const JourneyDataContext = createContext<JourneyDataContextType | undefined>(
  undefined,
);

export function useJourneyData() {
  const ctx = useContext(JourneyDataContext);

  if (!ctx) {
    // During hydration, context might not be available yet
    // Return a minimal fallback to prevent crashes
    return {
      userData: {
        questions: [],
        recommendations: [],
      },
      refresh: async () => {
        // eslint-disable-next-line no-console
        console.warn("Refresh called before context is available");
      },
      updateUserData: () => {
        // eslint-disable-next-line no-console
        console.warn("UpdateUserData called before context is available");
      },
    };
  }

  return ctx;
}

export function JourneyDataClientProvider({
  userData: initialUserData,
  children,
}: {
  userData: PublicJourneyData;
  children: React.ReactNode;
}) {
  const [userData, setUserData] = useState<PublicJourneyData>(initialUserData);

  // Helper to refresh the data from the API
  const refresh = useCallback(async () => {
    const res = await fetch("/api/load-journey", {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      cache: "no-store",
    });

    if (!res.ok) throw new Error("Failed to reload journey data");
    const fresh = await res.json();

    setUserData(fresh);
  }, []);

  // Helper to update the data directly (for local changes)
  const updateUserData = useCallback((newData: PublicJourneyData) => {
    setUserData(newData);
  }, []);

  return (
    <JourneyDataContext.Provider value={{ userData, refresh, updateUserData }}>
      {children}
    </JourneyDataContext.Provider>
  );
}
